import { getAvgSessionDuration } from "@/lib/analyticsDB";
import type { AnalyticsSession } from "@/lib/analyticsDB";

interface StatsCardsProps {
  sessions: AnalyticsSession[];
}

function StatCard({
  label,
  value,
  subtext,
  icon,
  accent,
}: {
  label: string;
  value: string;
  subtext: string;
  icon: string;
  accent: string;
}) {
  return (
    <div className="glass-card p-4 md:p-5 flex items-start gap-4">
      <div className={`w-10 h-10 flex items-center justify-center rounded-lg flex-shrink-0 ${accent}`}>
        <i className={`${icon} text-lg`} />
      </div>
      <div>
        <p className="text-xs text-silver-400 mb-0.5">{label}</p>
        <p className="text-xl md:text-2xl font-bold text-white">{value}</p>
        <p className="text-[11px] text-silver-500 mt-0.5">{subtext}</p>
      </div>
    </div>
  );
}

export default function StatsCards({ sessions }: StatsCardsProps) {
  const total = sessions.length;
  const completed = sessions.filter((s) => s.completed).length;
  const submitted = sessions.filter((s) => s.submittedForm).length;
  const demoBooked = sessions.filter((s) => s.demoBooked).length;
  const trialStarted = sessions.filter((s) => s.trialStarted).length;
  const conversionActions = demoBooked + trialStarted;
  const completionRate = total > 0 ? Math.round((completed / total) * 100) : 0;
  const advisorConversionRate = total > 0 ? Math.round((conversionActions / total) * 100) : 0;
  const avgDuration = getAvgSessionDuration(sessions);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
      <StatCard
        label="Total Sessions"
        value={String(total)}
        subtext="All Product Advisor interactions"
        icon="ri-user-line"
        accent="bg-emerald-accent/10 text-emerald-accent border border-emerald-accent/20"
      />
      <StatCard
        label="Completion Rate"
        value={`${completionRate}%`}
        subtext={`${completed} of ${total} reached recommendations`}
        icon="ri-funds-line"
        accent="bg-amber-500/10 text-amber-400 border border-amber-500/20"
      />
      <StatCard
        label="Demo Requests"
        value={String(demoBooked)}
        subtext={`${submitted} forms submitted`}
        icon="ri-calendar-check-line"
        accent="bg-blue-500/10 text-blue-400 border border-blue-500/20"
      />
      <StatCard
        label="Trial Starts"
        value={String(trialStarted)}
        subtext="From recommendation screen"
        icon="ri-rocket-line"
        accent="bg-teal-500/10 text-teal-400 border border-teal-500/20"
      />
      <StatCard
        label="Advisor Conversion"
        value={`${advisorConversionRate}%`}
        subtext={`${conversionActions} total actions from ${total} sessions`}
        icon="ri-arrow-up-circle-line"
        accent="bg-emerald-600/10 text-emerald-400 border border-emerald-600/20"
      />
      <StatCard
        label="Avg. Duration"
        value={`${avgDuration}s`}
        subtext="Time from open to close"
        icon="ri-time-line"
        accent="bg-orange-500/10 text-orange-400 border border-orange-500/20"
      />
    </div>
  );
}
